import React, { useState } from "react";
import CustomSearch from "../../components/common/CustomSearch";
import CustomDropdownMenu from "../../components/CustomDropdownMenu";
import CustomChip from "../../components/common/CustomChip";
import CustomButton from "../../components/common/CustomButton/CustomButton";
import { useNavigate } from "react-router-dom";
import { Eye } from "lucide-react";
import { allLeadsUrl } from "../../utils/urls";
import ServerPaginationDataGrid from "../../components/common/CustomDataGrid/ServerPaginationDataGrid";

const STATUS_VARIANT = {
  New:      "primary",
  Assigned: "pending",
  Sold:     "success",
  Lost:     "error",
};

const PRIORITY_VARIANT = {
  High:   "error",
  Medium: "warning",
  Low:    "success",
};

const AllLeads = ({ category, companyList = [], loggedInUser = null }) => {
  const navigate = useNavigate();
  const [selectedInsuranceProvider, setSelectedInsuranceProvider] = useState("Company");
  const [selectedPriority, setSelectedPriority] = useState("Priority");
  const [selectedStatus, setSelectedStatus] = useState("Status");
  const [search, setSearch] = useState("");

  const columns = [
    { field: "lead_name",                           headerName: "Lead Name" },
    { field: "lead_display_id",                     headerName: "Lead ID" },
    { field: "lead_product_relation_display_id",    headerName: "LPR ID" },
    { field: "company_name",                        headerName: "Company" },
    {
      field: "product_name",
      headerName: "Product Interest",
      renderCell: (row) => (
        <span className="text-sm font-medium text-ink-safe">
          {row.is_lead_without_product
            ? row?.sub_category_title || row?.category_title
            : row?.product_name}
        </span>
      ),
    },
    {
      field: "priority",
      headerName: "Priority",
      renderCell: (row) => (
        <CustomChip
          variant={PRIORITY_VARIANT[row.priority] ?? "default"}
          label={row.priority || "N/A"}
          size="sm"
        />
      ),
    },
    {
      field: "lead_status_title",
      headerName: "Status",
      renderCell: (row) => (
        <CustomChip
          variant={STATUS_VARIANT[row.lead_status_title] ?? "default"}
          label={row.lead_status_title || "N/A"}
          size="sm"
        />
      ),
    },
    {
      field: "action",
      headerName: "Action",
      renderCell: (row) => (
        <CustomButton
          label="View"
          variant="text"
          startIcon={<Eye className="w-4 h-4" />}
          size="sm"
          onClick={() => navigate("/leads/details", { state: { lead: row } })}
        />
      ),
    },
  ];

  const hasFilter =
    selectedInsuranceProvider !== "Company" ||
    selectedPriority !== "Priority" ||
    selectedStatus !== "Status" ||
    !!search;

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between gap-2 p-2 mb-2">
        <div className="flex-1">
          <CustomSearch
            className="!w-full border border-primary"
            placeholder="Search leads"
            onChange={(data) => setSearch(data)}
          />
        </div>
        <div className="flex items-center gap-2">
          {!loggedInUser?.rm_id && (
            <CustomDropdownMenu
              position="right"
              label={
                companyList.find((e) => e.company_id === selectedInsuranceProvider)
                  ?.company_name || "Company"
              }
              list={companyList?.map((company) => ({
                label: company.company_name,
                onClick: () => setSelectedInsuranceProvider(company.company_id),
              }))}
            />
          )}
          <CustomDropdownMenu
            label={selectedPriority}
            list={[
              { label: "High",   onClick: () => setSelectedPriority("High")   },
              { label: "Medium", onClick: () => setSelectedPriority("Medium") },
              { label: "Low",    onClick: () => setSelectedPriority("Low")    },
            ]}
            height="auto"
          />
          <CustomDropdownMenu
            label={selectedStatus}
            list={[
              { label: "New",      onClick: () => setSelectedStatus("New")      },
              { label: "Assigned", onClick: () => setSelectedStatus("Assigned") },
              { label: "Sold",     onClick: () => setSelectedStatus("Sold")     },
              { label: "Lost",     onClick: () => setSelectedStatus("Lost")     },
            ]}
            height="auto"
          />
        </div>
      </div>

      <ServerPaginationDataGrid
        url={allLeadsUrl}
        requestParameter={{
          category_id: category,
          company_id: selectedInsuranceProvider !== "Company" ? selectedInsuranceProvider : null,
          status:     selectedStatus   !== "Status"   ? selectedStatus   : null,
          priority:   selectedPriority !== "Priority" ? selectedPriority : null,
        }}
        search={search}
        columns={columns}
        isServerPagination={true}
        keyId="categoryId"
        dataMapper={(list) => ({
          data: list?.data?.map((item) => ({ ...item })),
          metadata: list?.metadata,
        })}
        emptyVariant={hasFilter ? "no-results" : "first-use"}
        emptyTitle={hasFilter ? "No leads match" : "No leads yet"}
        emptyDescription={
          hasFilter
            ? "Try a different filter, company, or search term."
            : "Leads will appear here once they are added."
        }
      />
    </div>
  );
};

export default AllLeads;
